/**
 * Image management service
 *  FIXED - Aligned with backend ImageController.js
 * version: 2.1.0 - Compatible with routes /API/images/*
 */
/// <reference types="vite/client" />

import { apiService } from './apiService';
import { normalizeImageUrl } from '../utils/imageUrl';

export interface ImageValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ImageUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface ImageMetadata {
  alt_text?: string;
  is_primary?: boolean; // ✅ fix - is_primary instead of is_main
  display_order?: number; // ✅ fix - display_order instead of order_index
  description?: string;
}

//  interface aligned with the backend schema
export interface ProjectImageData {
  id: string;
  project_id: string;
  image_url: string;
  image_name: string;
  file_size: number;
  mime_type: string;
  is_primary: boolean;
  display_order: number;
  alt_text?: string;
  created_at: string;
  updated_at: string;
  url?: string;
  thumbnail_url?: string;
}

//  Interfaces to type backend API responses
interface ApiResponseData {
  images?: any[];
  data?: any[] | any;
  metadata?: any;
  [key: string]: any;
}

interface ApiResponse {
  data?: ApiResponseData | any[] | null;
  success?: boolean;
  message?: string;
  error?: string;
  [key: string]: any;
}

class ImageService {
  private readonly DEFAULT_LIMITS = {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxFiles: 5,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
    maxWidth: 1920,
    maxHeight: 1080
  };

  //  CRITICAL fix - Base URL aligned with the backend
  private readonly API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

  // ================================
  // VALIDATION
  // ================================

  validateImage(file: File): ImageValidationResult {
    const result: ImageValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (file.size > this.DEFAULT_LIMITS.maxFileSize) {
      result.isValid = false;
      result.errors.push(`Fichier trop volumineux (${this.formatFileSize(file.size)}). Limite: ${this.formatFileSize(this.DEFAULT_LIMITS.maxFileSize)}`);
    }

    if (!this.DEFAULT_LIMITS.allowedTypes.includes(file.type)) {
      result.isValid = false;
      result.errors.push(`Type non supporté (${file.type}). Types acceptés: ${this.DEFAULT_LIMITS.allowedTypes.join(', ')}`);
    }

    if (file.size > 5 * 1024 * 1024) {
      result.warnings.push('Fichier volumineux, compression recommandée');
    }

    if (file.name.length > 255) {
      result.isValid = false;
      result.errors.push('Nom de fichier trop long (max 255 caractères)');
    }

    return result;
  }

  validateImages(files: File[]): ImageValidationResult {
    const result: ImageValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (files.length > this.DEFAULT_LIMITS.maxFiles) {
      result.isValid = false;
      result.errors.push(`Trop de fichiers (${files.length}). Limite: ${this.DEFAULT_LIMITS.maxFiles}`);
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = this.DEFAULT_LIMITS.maxFileSize * this.DEFAULT_LIMITS.maxFiles;

    if (totalSize > maxTotalSize) {
      result.isValid = false;
      result.errors.push(`Taille totale trop importante (${this.formatFileSize(totalSize)}). Limite: ${this.formatFileSize(maxTotalSize)}`);
    }

    files.forEach((file, index) => {
      const fileValidation = this.validateImage(file);

      if (!fileValidation.isValid) {
        result.isValid = false;
        fileValidation.errors.forEach(error => {
          result.errors.push(`Fichier ${index + 1} (${file.name}): ${error}`);
        });
      }

      fileValidation.warnings.forEach(warning => {
        result.warnings.push(`Fichier ${index + 1} (${file.name}): ${warning}`);
      });
    });

    return result;
  }

  // ================================
  // UTILITIES
  // ================================

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  //  CRITICAL fix - URL aligned with route /API/images/:filename
  getImageUrl(filename: string | null | undefined): string {
    if (!filename) {
      return '/placeholder-image.svg';
    }

    // URLs complètes ou chemins déjà préfixés : on se contente de normaliser
    // (corrige notamment le double préfixe accidentel "/api/api/").
    if (
      filename.toLowerCase().startsWith('http://') ||
      filename.toLowerCase().startsWith('https://') ||
      filename.startsWith('/')
    ) {
      return normalizeImageUrl(filename);
    }

    //  fix - Use the exact backend route
    return `${this.API_BASE_URL}/images/${filename}`;
  }

  generateUniqueFilename(originalName: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');

    return `${nameWithoutExt}_${timestamp}_${random}.${extension}`;
  }

  previewImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
  reject(new Error('Le fichier n\'est pas une image'));
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };

      reader.onerror = () => {
  reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsDataURL(file);
    });
  }

  getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
  reject(new Error('Impossible de lire les dimensions de l\'image'));
      };

      img.src = url;
    });
  }

  async resizeImage(file: File, options: ImageUploadOptions): Promise<File> {
    return new Promise(async (resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'));
          return;
        }

        img.onload = () => {
          const { maxWidth = this.DEFAULT_LIMITS.maxWidth, maxHeight = this.DEFAULT_LIMITS.maxHeight } = options;

          let { width, height } = img;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          // Resize with high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: options.format ? `image/${options.format}` : file.type,
                  lastModified: Date.now()
                });
                resolve(resizedFile);
              } else {
        reject(new Error('Erreur lors du redimensionnement'));
              }
            },
            options.format ? `image/${options.format}` : file.type,
            options.quality || 0.8
          );
        };

    img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'));
        img.src = URL.createObjectURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }

  // ================================
  // UPLOAD -  FIXED according to ImageController.js
  // ================================

  async uploadSingle(file: File, options?: ImageUploadOptions): Promise<any> {

    const validation = this.validateImage(file);
    if (!validation.isValid) {
      throw new Error(`Validation échouée: ${validation.errors.join(', ')}`);
    }

    let processedFile = file;
    if (options) {
      processedFile = await this.resizeImage(file, options);
    }

    try {
      const formData = new FormData();
  formData.append('image', processedFile); // ✅ fix - 'image' as per ImageController

  //  fix - exact backend route
      const result = await apiService.upload('/images/upload', formData);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async uploadMultiple(files: File[], options?: ImageUploadOptions): Promise<any[]> {

  const validation = this.validateImages(files);
  if (!validation.isValid) {
    throw new Error(`Validation échouée: ${validation.errors.join(', ')}`);
  }

  let processedFiles = files;
  if (options) {
    processedFiles = await Promise.all(
      files.map(file => this.resizeImage(file, options))
    );
  }

  try {
    const formData = new FormData();
    processedFiles.forEach(file => {
      formData.append('images', file);
    });

    const result = await apiService.upload('/images/multiple', formData);

    //  fix - typed safe handling of the response
    if (result && result.success) {
      // Validate if result.data exists and is an object
      if (result.data && typeof result.data === 'object') {
        // Validate if result.data.results exists and is an array
        if ('results' in result.data && Array.isArray(result.data.results)) {
          return result.data.results
            .filter((r: any) => r && typeof r === 'object' && r.success)
            .map((r: any) => r.data || r);
        }

        // If result.data is directly an array
        if (Array.isArray(result.data)) {
          return result.data;
        }

        // If result.data is a single object, return it in an array
        return [result.data];
      }
    }

    //  Safe fallback
    return [];

  } catch (error) {
    throw error;
  }
}

  async addToProject(
    projectId: string,
    file: File,
    metadata?: ImageMetadata,
    options?: ImageUploadOptions
  ): Promise<any> {

    const validation = this.validateImage(file);
    if (!validation.isValid) {
      throw new Error(`Validation échouée: ${validation.errors.join(', ')}`);
    }

    let processedFile = file;
    if (options) {
      processedFile = await this.resizeImage(file, options);
    }

    try {
      const formData = new FormData();
      formData.append('image', processedFile); // ✅ correction - 'image' selon ImageController

      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
          if (value !== undefined) {
            formData.append(key, String(value));
          }
        });
      }

      //  correction - route exacte du backend
      const result = await apiService.upload(`/images/project/${projectId}`, formData);
      return result;
    } catch (error) {
      throw error;
    }
  }

  // ================================
  // HANDLING -  FIXED according to ImageController.js
  // ================================

  /**
   *  UTILITY FUNCTION - Safe property verification
   */
  private hasProperty(obj: any, property: string): boolean {
    return obj && typeof obj === 'object' && property in obj;
  }

  /**
   *  UTILITY FUNCTION - Safe extraction of an array
   */
  private extractArrayFromResponse(response: any, paths: string[]): any[] {
    // Validate if response is directly an array
    if (Array.isArray(response)) {
      return response;
    }

    // Walk through possible paths
    for (const path of paths) {
      const keys = path.split('.');
      let current = response;

      try {
        for (const key of keys) {
          if (this.hasProperty(current, key)) {
            current = current[key];
          } else {
            current = null;
            break;
          }
        }

        if (Array.isArray(current)) {
          return current;
        }
      } catch (error) {
        continue;
      }
    }

  // If no array found, try to search dynamically
    if (response && typeof response === 'object') {
      const searchInObject = (obj: any, depth = 0): any[] | null => {
    if (depth > 3) return null; // Limit search depth

        for (const key of Object.keys(obj)) {
          try {
            const value = obj[key];
            if (Array.isArray(value)) {
              return value;
            }
            if (value && typeof value === 'object') {
              const result = searchInObject(value, depth + 1);
              if (result) return result;
            }
          } catch (error) {
            continue;
          }
        }
        return null;
      };

  const dynamicResult = searchInObject(response);
      if (dynamicResult) {
        return dynamicResult;
      }
    }

    return [];
  }

  /**
   *  FIXED - Get project images according to ImageController.js
   */
  async getProjectImages(projectId: string): Promise<ProjectImageData[]> {
    try {

      //  CRITICAL fix - exact backend route
      const response = await apiService.get(`/images/project/${projectId}`, false) as ApiResponse;
      //  fix - Extraction paths according to ImageController response
      const possiblePaths = [
        'data.images',      // main backend structure
        'data.data',        // possible double encapsulation
        'data',             // direct data
        'images',           // direct images property
        'items',            // alternative
        'results'           // alternative
      ];

      const imagesData = this.extractArrayFromResponse(response, possiblePaths);

      if (imagesData.length === 0) {
        return [];
      }

  //  safe mapping based on fields returned by ImageController
      const mappedImages = imagesData
        .filter((img: any, index: number) => {
          if (!img || typeof img !== 'object') {
            return false;
          }
          if (!img.id) {
            return false;
          }
          return true;
        })
    .map((img: any, index: number): ProjectImageData => {
          try {
      //  mapping based on the structure returned by ImageController.getProjectImages
            const mappedImage: ProjectImageData = {
              id: String(img.id || `temp_${index}`),
              project_id: String(img.projectId || img.project_id || projectId),
              image_url: String(img.imageUrl || img.image_url || ''),
              image_name: String(img.imageName || img.image_name || 'unknown'),
              file_size: Number(img.fileSize || img.file_size || 0),
              mime_type: String(img.mimeType || img.mime_type || 'image/jpeg'),
              is_primary: Boolean(img.isPrimary || img.is_primary || false),
              display_order: Number(img.displayOrder || img.display_order || index),
              alt_text: String(img.altText || img.alt_text || ''),
              created_at: String(img.createdAt || img.created_at || new Date().toISOString()),
              updated_at: String(img.updatedAt || img.updated_at || new Date().toISOString()),
              //  URLs computed with the fixed method
              url: this.getImageUrl(img.imageUrl || img.image_url || img.imageName || img.image_name),
              thumbnail_url: this.getImageUrl(img.thumbnailUrl || img.thumbnail_url || img.imageUrl || img.image_url)
            };

            return mappedImage;
          } catch (mappingError) {
            //  robust default object
            return {
              id: `error_${index}_${Date.now()}`,
              project_id: String(projectId),
              image_url: '/error-image.svg',
              image_name: 'Image corrompue',
              file_size: 0,
              mime_type: 'image/jpeg',
              is_primary: false,
              display_order: index,
              alt_text: 'Image non disponible',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              url: '/error-image.svg',
              thumbnail_url: '/error-image.svg'
            };
          }
        });

      //  safe sort
      mappedImages.sort((a, b) => {
        // primary sort by display_order
        if (a.display_order !== b.display_order) {
          return a.display_order - b.display_order;
        }

        // secondary sort by creation date
        try {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();

          if (!isNaN(dateA) && !isNaN(dateB)) {
            return dateA - dateB;
          }
        } catch (error) {
          }

        // sort by ID as a last resort
        return a.id.localeCompare(b.id);
      });

      return mappedImages;

    } catch (error) {
      //  Detailed error logging
      if (error && typeof error === 'object') {
        const errorObj = error as any;
        }

      //  Return an empty array rather than throwing an error
      return [];
    }
  }

  // ================================
  // OTHER METHODS -  FIXED according to ImageController.js
  // ================================

  async deleteImage(imageId: string): Promise<void> {
    try {
  //  fix - exact backend route
      await apiService.delete(`/images/${imageId}`);
    } catch (error) {
      throw error;
    }
  }

  async updateMetadata(imageId: string, metadata: ImageMetadata): Promise<any> {
    try {
  //  fix - exact backend route
      const result = await apiService.put(`/images/${imageId}`, metadata);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async reorderProjectImages(projectId: string, imageOrders: { id: string; display_order: number }[]): Promise<any> {
    try {
  //  fix - exact backend route
      const result = await apiService.put(`/images/project/${projectId}/reorder`, { imageOrders });
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getUploadLimits(): Promise<any> {
    try {
      //  correction - route exacte du backend
      const response = await apiService.get('/images/limits', false);
      return response.data || this.DEFAULT_LIMITS;
    } catch (error) {
      return this.DEFAULT_LIMITS;
    }
  }

  // ================================
  // LIMITS AND CONFIGURATION
  // ================================

  get defaultLimits() {
    return { ...this.DEFAULT_LIMITS };
  }

  get allowedMimeTypes(): string[] {
    return [...this.DEFAULT_LIMITS.allowedTypes];
  }

  isFileTypeAllowed(file: File): boolean {
    return this.DEFAULT_LIMITS.allowedTypes.includes(file.type);
  }

  getRecommendedExtension(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif'
    };

    return mimeToExt[mimeType] || 'jpg';
  }

  cleanup(): void {
  }
}

//  singleton instance
export const imageService = new ImageService();
export default imageService;

// Automatic cleanup
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageService.cleanup();
  });
}
